import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ExternalLink, RefreshCw, Loader2, AlertTriangle, CheckCircle2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

// Extract 21 CFR / ISO / IEC citations from a body of text.
const CITE_REGEX =
  /\b(21 CFR \d+(?:\.\d+(?:\([a-z0-9]+\))?)?|ISO \d{4,5}(?:-\d+)?|IEC \d{4,5}(?:-\d+)?)\b/g;

export function extractCitations(text) {
  if (!text) return [];
  const s = new Set();
  const norm = (r) => r.replace(/\([a-z0-9]+\)$/, "").trim();  // strip subsection for grouping
  let m;
  CITE_REGEX.lastIndex = 0;
  while ((m = CITE_REGEX.exec(text)) !== null) s.add(norm(m[1]));
  return Array.from(s).sort();
}

/**
 * Given a set of documents (with .content + .created_at), extract citations
 * and check eCFR for the latest amendment date. Flag stale documents.
 */
export default function RegulationAlerts({ documents }) {
  const [status, setStatus] = useState({}); // ref → row
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const citations = React.useMemo(() => {
    const map = new Map(); // ref → oldest doc created_at
    for (const d of documents || []) {
      const refs = extractCitations(d.content || "");
      for (const r of refs) {
        const prev = map.get(r);
        if (!prev || new Date(d.created_at) < new Date(prev)) {
          map.set(r, d.created_at);
        }
      }
    }
    return Array.from(map.entries()).map(([ref, since]) => ({ ref, since }));
  }, [documents]);

  const runCheck = async () => {
    if (citations.length === 0) return;
    setLoading(true);
    try {
      // group by earliest doc since (worst case), send full ref list
      const earliest = citations.reduce((a, c) => (!a || new Date(c.since) < new Date(a) ? c.since : a), null);
      const { data } = await api.post("/regulations/check", {
        refs: citations.map((c) => c.ref),
        since: earliest,
      });
      const map = {};
      for (const r of data.results) map[r.ref] = r;
      setStatus(map);
      const stale = data.results.filter((r) => r.needs_review).length;
      if (stale > 0) toast.warning(`${stale} regulation${stale === 1 ? "" : "s"} amended since your draft`);
      else toast.success("All cited regulations look current");
    } catch {
      toast.error("Could not check eCFR");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { setStatus({}); }, [citations.length]);

  if (citations.length === 0) return null;

  const staleCount = Object.values(status).filter((r) => r.needs_review).length;

  return (
    <div className="border border-slate-200 bg-white" data-testid="reg-alerts">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-6 py-4 text-left hover:bg-slate-50"
      >
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-4 w-4 text-[#2563EB]" />
          <div>
            <div className="overline text-[#2563EB]">REGULATION FRESHNESS</div>
            <div className="mt-0.5 text-sm text-slate-700">
              {citations.length} unique citation{citations.length === 1 ? "" : "s"} across this project
              {Object.keys(status).length > 0 && (
                <>
                  {" · "}
                  {staleCount > 0 ? (
                    <span className="text-amber-300">{staleCount} may need review</span>
                  ) : (
                    <span className="text-emerald-300">all current</span>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="reg-alerts-check-btn"
            onClick={(e) => { e.stopPropagation(); runCheck(); }}
            disabled={loading}
            className="inline-flex items-center gap-2 border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-[#2563EB] disabled:opacity-50"
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Check eCFR
          </button>
          <span className="overline text-slate-400">{expanded ? "HIDE" : "SHOW"}</span>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-slate-200 p-6">
          <div className="grid gap-2">
            {citations.map(({ ref, since }) => {
              const s = status[ref];
              const stale = s?.needs_review;
              return (
                <div
                  key={ref}
                  data-testid="reg-alert-row"
                  className={`flex flex-wrap items-center justify-between gap-3 border px-4 py-3 text-sm ${
                    stale ? "border-amber-500/40 bg-amber-500/5" : "border-slate-200 bg-[#FAFAF7]"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {s ? (
                      stale ? <AlertTriangle className="h-4 w-4 text-amber-400" /> :
                              <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                    ) : <div className="h-4 w-4 border border-slate-200" />}
                    <span className="font-mono text-[#93C5FD]">{ref}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500">
                    <span>Drafted {new Date(since).toLocaleDateString()}</span>
                    {s?.latest_amended && (
                      <span className={stale ? "text-amber-300" : ""}>
                        eCFR amended {s.latest_amended}
                      </span>
                    )}
                    {s?.ecfr_url && (
                      <a
                        href={s.ecfr_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[#93C5FD] hover:text-slate-900"
                      >
                        View <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
